import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import {
  type ScheduledMealOptionsDto,
  type SiteOrderOptionsDto,
} from '../dto/order-options/site-order-options-dto';
import { type ResponseDto } from '../dto/ResponseDto';
import { SITE_ORDER_OPTIONS_END_POINT_URL } from '../utils/endpoints';
import { type LocalDate } from '../utils/local-date';
import type ProblemDetailsDto from '../dto/error/problem-details-dto';

export interface SiteOrderOptionsResult {
  meals: ScheduledMealOptionsDto[];
  isLoading: boolean;
  errorMessage?: string;
}

const EMPTY_OPTIONS = { meals: [] };

/**
 * Loads what site "siteId" can be ordered on "orderDate" from the site controller: the meals scheduled for it which
 * its customer may order, each carrying the beverages and supplies it may be ordered with. It loads again whenever
 * the site or the date changes.
 */
const useSiteOrderOptions = (
  siteId: string | undefined,
  orderDate: LocalDate | undefined
): SiteOrderOptionsResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<SiteOrderOptionsResult>({
    ...EMPTY_OPTIONS,
    isLoading: false,
  });
  const orderDateText = orderDate?.toISOString();

  useEffect(() => {
    if (!siteId || !orderDateText) {
      setState({ ...EMPTY_OPTIONS, isLoading: false });
      return;
    }
    setState((previous) => ({ ...previous, isLoading: true, errorMessage: undefined }));

    const subscription = api
      .get(SITE_ORDER_OPTIONS_END_POINT_URL(siteId), { orderDate: orderDateText }, {})
      .pipe(
        tap((response: ResponseDto<SiteOrderOptionsDto>) => {
          setState({ meals: response.data.meals, isLoading: false });
        }),
        catchError((error: ProblemDetailsDto) => {
          // Clear the options, so the ones loaded for another site or date aren't offered here
          setState({
            ...EMPTY_OPTIONS,
            isLoading: false,
            errorMessage: error.detail ?? 'Failed to load the meals available for this site',
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if the site or the date changes, or the component unmounts, before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [siteId, orderDateText]);

  return state;
};

export default useSiteOrderOptions;
